import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORIES } from "@/lib/categories";
import { PRESUPUESTOS, HORIZONTES, DEPENDIENTES } from "@/lib/prequalConfig";
import { PREQUAL } from "@/constants/testIds";

// -------- Shared UI atoms ---------

const StepHeader = ({ children, hint }) => (
  <>
    <h3 className="font-serif text-3xl lg:text-4xl text-razmek-dark">
      {children}
    </h3>
    {hint && (
      <p className="font-sans text-sm text-razmek-gray mt-2">{hint}</p>
    )}
  </>
);

const OptionButton = ({ selected, onClick, testId, children, className = "" }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`p-5 border-2 text-left transition-all ${
      selected
        ? "border-razmek-gold bg-razmek-gold/5 text-razmek-blue"
        : "border-razmek hover:border-razmek-blue text-razmek-dark"
    } ${className}`}
  >
    {children}
  </button>
);

const underlineInputCls =
  "mt-2 border-0 border-b border-razmek rounded-none focus-visible:ring-0 focus-visible:border-razmek-gold px-0 font-sans";

// -------- Step components ---------

export const ProteccionStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Selecciona la categoría que más se acerque a tu necesidad.">
      ¿Qué deseas <span className="italic text-razmek-blue">proteger</span>?
    </StepHeader>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
      {CATEGORIES.map((c) => {
        const Icon = c.icon;
        const selected = data.proteccion === c.slug;
        return (
          <button
            key={c.slug}
            type="button"
            data-testid={PREQUAL.field(`proteccion-${c.slug}`)}
            onClick={() => update("proteccion", c.slug)}
            className={`flex items-center gap-3 p-5 border-2 transition-all text-left ${
              selected
                ? "border-razmek-gold bg-razmek-gold/5"
                : "border-razmek hover:border-razmek-blue"
            }`}
          >
            <Icon
              size={22}
              strokeWidth={1.4}
              className={selected ? "text-razmek-gold" : "text-razmek-blue"}
            />
            <span className="font-serif text-lg text-razmek-dark">{c.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

export const EdadStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Para calibrar correctamente la propuesta.">
      ¿Cuál es tu <span className="italic text-razmek-blue">edad</span>?
    </StepHeader>
    <div className="mt-10 max-w-sm">
      <Label className="eyebrow text-razmek-gray">Edad en años</Label>
      <Input
        type="number"
        min={18}
        max={100}
        data-testid={PREQUAL.field("edad")}
        value={data.edad}
        onChange={(e) => update("edad", e.target.value)}
        className={`${underlineInputCls} font-serif text-3xl`}
        placeholder="35"
      />
    </div>
  </div>
);

export const EstadoStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Estado o ciudad. Algunos productos varían por jurisdicción.">
      ¿Dónde <span className="italic text-razmek-blue">resides</span>?
    </StepHeader>
    <div className="mt-10 max-w-md">
      <Label className="eyebrow text-razmek-gray">Estado / Ciudad</Label>
      <Input
        type="text"
        data-testid={PREQUAL.field("estado")}
        value={data.estado}
        onChange={(e) => update("estado", e.target.value)}
        className={`${underlineInputCls} font-serif text-2xl`}
        placeholder="Ciudad de México"
      />
    </div>
  </div>
);

export const PresupuestoStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Rango mensual orientativo. No es un compromiso.">
      ¿Cuál es tu <span className="italic text-razmek-blue">presupuesto</span> aproximado?
    </StepHeader>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
      {PRESUPUESTOS.map((p) => (
        <OptionButton
          key={p.v}
          selected={data.presupuesto === p.v}
          onClick={() => update("presupuesto", p.v)}
          testId={PREQUAL.field(`presupuesto-${p.v}`)}
          className="font-serif text-lg"
        >
          {p.label}
        </OptionButton>
      ))}
    </div>
  </div>
);

export const HorizonteStep = ({ data, update }) => (
  <div>
    <StepHeader hint="El plazo en el que esperas concretar tu objetivo.">
      ¿Qué <span className="italic text-razmek-blue">horizonte</span> contemplas?
    </StepHeader>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-8">
      {HORIZONTES.map((h) => (
        <OptionButton
          key={h.v}
          selected={data.horizonte === h.v}
          onClick={() => update("horizonte", h.v)}
          testId={PREQUAL.field(`horizonte-${h.v}`)}
          className="font-serif text-lg"
        >
          {h.label}
        </OptionButton>
      ))}
    </div>
  </div>
);

export const DependientesStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Personas que dependen total o parcialmente de tu ingreso.">
      ¿Cuántos <span className="italic text-razmek-blue">dependientes</span> económicos tienes?
    </StepHeader>
    <div className="grid grid-cols-5 gap-3 mt-8 max-w-md">
      {DEPENDIENTES.map((d) => (
        <OptionButton
          key={d.v}
          selected={data.dependientes === d.v}
          onClick={() => update("dependientes", d.v)}
          testId={PREQUAL.field(`dependientes-${d.v}`)}
          className="font-serif text-2xl text-center px-0"
        >
          {d.v}
        </OptionButton>
      ))}
    </div>
  </div>
);

export const ContactoStep = ({ data, update }) => (
  <div>
    <StepHeader hint="Un asesor revisará tu caso y te escribirá en menos de 24h hábiles.">
      ¿Cómo te <span className="italic text-razmek-blue">contactamos</span>?
    </StepHeader>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
      <div>
        <Label className="eyebrow text-razmek-gray">Nombre completo</Label>
        <Input
          data-testid={PREQUAL.field("nombre")}
          value={data.nombre}
          onChange={(e) => update("nombre", e.target.value)}
          className={underlineInputCls}
        />
      </div>
      <div>
        <Label className="eyebrow text-razmek-gray">Teléfono</Label>
        <Input
          data-testid={PREQUAL.field("telefono")}
          value={data.telefono}
          onChange={(e) => update("telefono", e.target.value)}
          className={underlineInputCls}
        />
      </div>
      <div className="md:col-span-2">
        <Label className="eyebrow text-razmek-gray">Correo electrónico</Label>
        <Input
          type="email"
          data-testid={PREQUAL.field("email")}
          value={data.email}
          onChange={(e) => update("email", e.target.value)}
          className={underlineInputCls}
        />
      </div>
      <div className="md:col-span-2">
        <Label className="eyebrow text-razmek-gray">Mensaje (opcional)</Label>
        <Textarea
          data-testid={PREQUAL.field("mensaje")}
          value={data.mensaje}
          onChange={(e) => update("mensaje", e.target.value)}
          rows={3}
          className="mt-2 border border-razmek rounded-none focus-visible:ring-0 focus-visible:border-razmek-gold font-sans resize-none"
        />
      </div>
    </div>
  </div>
);

// Registry that maps step id -> component. Used by PrequalForm to render
// the current step without a giant if-else chain.
export const STEP_COMPONENTS = {
  proteccion: ProteccionStep,
  edad: EdadStep,
  estado: EstadoStep,
  presupuesto: PresupuestoStep,
  horizonte: HorizonteStep,
  dependientes: DependientesStep,
  contacto: ContactoStep,
};
